import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'
import { OrderStatus } from 'src/utils/enum'

// Actions - used with useDispatch
export const financeSlice = createSlice({
  name: 'finance',
  initialState: {
    bankaccount: null,
    pendingBankaccount: null,
    getPendingBankAccount: {
      status: null,
      error: null,
    },
    getFinanceTransactions: {
      status: null,
      error: null,
    },
    createFinanceTransactionPDF: {
      status: null,
      error: null,
    },
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getPendingBankAccount
      .addCase(getPendingBankAccount.pending, (state, action) => {
        state.getPendingBankAccount.status = 'loading'
      })
      .addCase(getPendingBankAccount.fulfilled, (state, action) => {
        state.getPendingBankAccount.status = 'succeeded'
        state.getPendingBankAccount.error = null // Clear any previous errors

        // Store getPendingBankAccount info
        if (action.payload.data.success) {
          state.pendingBankaccount =
            Object.keys(action.payload.data.data).length !== 0 ? action.payload.data.data : null
        }
      })
      .addCase(getPendingBankAccount.rejected, (state, action) => {
        state.getPendingBankAccount.status = 'failed'
        state.getPendingBankAccount.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear bankaccount info
        state.pendingBankaccount = null
      })
      // getFinanceTransactions
      .addCase(getFinanceTransactions.pending, (state, action) => {
        state.getFinanceTransactions.status = 'loading'
      })
      .addCase(getFinanceTransactions.fulfilled, (state, action) => {
        state.getFinanceTransactions.status = 'succeeded'
        state.getFinanceTransactions.error = null // Clear any previous errors

        // Store getFinanceTransactions info
      })
      .addCase(getFinanceTransactions.rejected, (state, action) => {
        state.getFinanceTransactions.status = 'failed'
        state.getFinanceTransactions.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear transactions info
      })
      // createFinanceTransactionPDF
      .addCase(createFinanceTransactionPDF.pending, (state, action) => {
        state.createFinanceTransactionPDF.status = 'loading'
      })
      .addCase(createFinanceTransactionPDF.fulfilled, (state, action) => {
        state.createFinanceTransactionPDF.status = 'succeeded'
        state.createFinanceTransactionPDF.error = null // Clear any previous errors

        // Store getFinanceTransactions info, if needed
      })
      .addCase(createFinanceTransactionPDF.rejected, (state, action) => {
        state.createFinanceTransactionPDF.status = 'failed'
        state.createFinanceTransactionPDF.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear transactions info, if needed
      })
  },
})
export const { clearStatus } = financeSlice.actions

export const getPendingBankAccount = createAsyncThunk(
  'finance/getPendingBankAccount',
  async (params, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      //const state = getState()
      const response = await client.get(`/finance/bank-account?pendingApproval=true`)

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getFinanceTransactions = createAsyncThunk(
  'finance/getFinanceTransactions',
  async (params, { rejectWithValue, getState }) => {
    try {
      params.startOfPeriod = params.startOfPeriod || '2024-01-01'
      params.endOfPeriod = params.endOfPeriod || '2024-12-31'
      params.page = params.page || 1
      params.limit = params.limit || 10

      const response = await client.get(
        `/finance/my-fund-transactions?startOfPeriod=${params.startOfPeriod}&endOfPeriod=${params.endOfPeriod}&page=${params.page}&limit=${params.limit}`,
      )

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const createFinanceTransactionPDF = createAsyncThunk(
  'finance/createFinanceTransactionPDF',
  async (params, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      //const state = getState()
      params.startOfPeriod = params.startOfPeriod || '2024-01-01'
      params.endOfPeriod = params.endOfPeriod || '2024-12-31'
      const response = await client.get(
        `/finance/my-fund-transactions/create-pdf?startOfPeriod=${params.startOfPeriod}&endOfPeriod=${params.endOfPeriod}&reportType=${params.reportType}`,
      )

      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Selector (Read data) - used with useSelector

export const selectGetFinanceTransactionStatus = (state) =>
  state.finance.getFinanceTransactions.status
export const selectGetFinanceTransactionError = (state) =>
  state.finance.getFinanceTransactions.error

export const selectCreateFinanceTransactionPDFStatus = (state) =>
  state.finance.createFinanceTransactionPDF.status
export const selectCreateFinanceTransactionPDFError = (state) =>
  state.finance.createFinanceTransactionPDF.error

export default financeSlice.reducer
